// Animated dotted-line world-map journey overlay.
// Shows the path walked so far (static, faded) and draws the new segment
// with a cartoon map-style animation using curved bezier paths.

import type { Difficulty } from "../data/difficulty";

const BASE = (import.meta.env.BASE_URL as string) ?? "/";

// Pixel coordinates in "display map.png" natural image space.
const WAYPOINTS = [
  { x: 0,    y: 0    },  // 0 – origin (top-left, off-map)
  { x: 434,  y: 130  },  // 1 – Freljord
  { x: 320,  y: 520  },  // 2 – Demacia
  { x: 1050, y: 334  },  // 3 – Noxus
  { x: 1382, y: 774  },  // 4 – Piltover
  { x: 1550, y: 1224 },  // 5 – Ixtal
  { x: 1164, y: 1256 },  // 6 – Shurima
  { x: 632,  y: 1304 },  // 7 – Targon
] as const;

// act number (1-7) → index into WAYPOINTS
const ACT_WAYPOINT = [0, 1, 2, 3, 4, 5, 6, 7] as const;

// How much each segment curves: fraction of the chord length offset perpendicular.
// Alternating sign gives the path a natural winding feel.
const CURVE = 0.22;

function segmentD(fromI: number, toI: number): string {
  const a = WAYPOINTS[fromI], b = WAYPOINTS[toI];
  const sign: number = fromI % 2 === 0 ? 1 : -1;
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  const offset = len * CURVE * sign;
  // Perpendicular unit vector (rotated 90° left from travel direction)
  const cx = (mx + (-dy / len) * offset).toFixed(1);
  const cy = (my + ( dx / len) * offset).toFixed(1);
  return `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
}

function ensureStyles(): void {
  if (document.getElementById("journey-anim-styles")) return;
  const s = document.createElement("style");
  s.id = "journey-anim-styles";
  s.textContent = `
    @keyframes journey-reveal      { to { stroke-dashoffset: 0; } }
    @keyframes journey-fade-in     { to { opacity: 1; } }
    @keyframes journey-travel      { from { offset-distance: 0%; } to { offset-distance: 100%; } }
    @keyframes journey-dest-pulse  {
      0%, 100% { opacity: 0.9; transform: scale(1); }
      50%      { opacity: 0.5; transform: scale(1.55); }
    }
  `;
  document.head.appendChild(s);
}

/**
 * Returns a positioned container showing the Runeterra world map with an
 * animated dotted-line journey trail up to `enteringAct`.
 *
 * @param enteringAct    The act number we are currently entering (1–6).
 * @param difficulty     Run difficulty — drives gold/red/deep-red trail color.
 */
export function createJourneyMapElement(enteringAct: number, difficulty: Difficulty = "normal"): HTMLElement {
  ensureStyles();

  const container = document.createElement("div");
  container.style.cssText =
    "position:relative;display:inline-block;line-height:0;" +
    "max-width:min(560px,90vw);overflow:hidden;border-radius:8px;";

  const img = document.createElement("img");
  img.src = `${BASE}images/map/display%20map.png`;
  img.style.cssText =
    "display:block;max-width:min(560px,90vw);max-height:60vh;object-fit:contain;";
  container.appendChild(img);

  const onLoad = () => buildOverlay(container, img, enteringAct, difficulty);
  img.onload = onLoad;
  if (img.complete && img.naturalWidth > 0) onLoad();

  return container;
}

// ── Core builder ───────────────────────────────────────────────────────────────

function buildOverlay(
  container: HTMLElement,
  img: HTMLImageElement,
  enteringAct: number,
  difficulty: Difficulty = "normal",
): void {
  container.querySelector("svg")?.remove();

  const W = img.naturalWidth;
  const H = img.naturalHeight;

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  svg.style.cssText =
    "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;";

  const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
  svg.appendChild(defs);

  const act      = Math.min(Math.max(enteringAct, 1), ACT_WAYPOINT.length - 1);
  const destIdx  = ACT_WAYPOINT[act];
  const fromIdx  = ACT_WAYPOINT[act - 1] ?? 0;

  const palette = difficulty === "impossible"
    ? {
        pastStroke:   "rgba(140,20,20,0.55)",
        pastDot:      "rgba(160,20,20,0.7)",
        liveStroke:   "rgba(255,40,40,1)",
        travelerFill: "rgba(255,80,80,1)",
        destFill:     "rgba(200,30,30,0.95)",
      }
    : difficulty === "hard"
    ? {
        pastStroke:   "rgba(180,40,40,0.45)",
        pastDot:      "rgba(180,40,40,0.6)",
        liveStroke:   "rgba(255,70,70,1)",
        travelerFill: "rgba(255,100,100,1)",
        destFill:     "rgba(220,60,60,0.9)",
      }
    : {
        pastStroke:   "rgba(200,155,60,0.4)",
        pastDot:      "rgba(200,155,60,0.55)",
        liveStroke:   "rgba(240,200,80,1)",
        travelerFill: "rgba(255,235,110,1)",
        destFill:     "rgba(240,200,80,0.9)",
      };
  const pastStroke   = palette.pastStroke;
  const pastDot      = palette.pastDot;
  const liveStroke   = palette.liveStroke;
  const travelerFill = palette.travelerFill;
  const destFill     = palette.destFill;

  // ── Previously traveled path (static, faded) ───────────────────────────────
  for (let i = 0; i < fromIdx; i++) {
    svg.appendChild(svgEl("path", {
      d: segmentD(i, i + 1),
      fill: "none",
      stroke: pastStroke,
      "stroke-width": "3.5",
      "stroke-dasharray": "7 6",
      "stroke-linecap": "round",
    }));
  }
  for (let i = 1; i <= fromIdx; i++) {
    const wp = WAYPOINTS[i];
    svg.appendChild(svgEl("circle", {
      cx: String(wp.x), cy: String(wp.y), r: "6",
      fill: pastDot,
    }));
  }

  // ── Animated new segment(s) ────────────────────────────────────────────────
  const SEG_DUR   = 1.5;
  const START_DLY = 0.35;
  let   delay     = START_DLY;

  // Collect mask paths so we can call getTotalLength() once in the DOM.
  const pending: Array<{ maskPath: SVGPathElement; delay: number; dur: number }> = [];

  for (let i = fromIdx; i < destIdx; i++) {
    const d       = segmentD(i, i + 1);
    const maskId  = `jm-${i}-${i + 1}`;

    // Mask: solid stroke animated to reveal the dotted visual below.
    const mask = svgEl("mask", { id: maskId }) as SVGMaskElement;
    mask.appendChild(svgEl("rect", {
      x: "0", y: "0", width: String(W), height: String(H), fill: "black",
    }));
    const maskPath = svgEl("path", {
      d,
      stroke: "white",
      "stroke-width": "26",
      fill: "none",
      "stroke-linecap": "round",
    }) as SVGPathElement;
    mask.appendChild(maskPath);
    defs.appendChild(mask);

    pending.push({ maskPath, delay, dur: SEG_DUR });

    // Visual dotted path, revealed progressively by the mask.
    svg.appendChild(svgEl("path", {
      d,
      fill: "none",
      stroke: liveStroke,
      "stroke-width": "4",
      "stroke-dasharray": "7 6",
      "stroke-linecap": "round",
      mask: `url(#${maskId})`,
    }));

    // Traveler dot moving along the curved segment.
    const traveler = svgEl("circle", {
      r: "7",
      fill: travelerFill,
      stroke: "rgba(255,255,255,0.75)",
      "stroke-width": "2",
    }) as SVGCircleElement;
    traveler.style.cssText =
      `opacity:0;` +
      `offset-path:path("${d}");` +
      `offset-distance:0%;` +
      `animation:journey-fade-in 0.05s linear ${delay}s forwards,` +
      `journey-travel ${SEG_DUR}s ease ${delay}s forwards;`;
    svg.appendChild(traveler);

    delay += SEG_DUR;
  }

  // ── Pulsing destination marker ─────────────────────────────────────────────
  const dest = WAYPOINTS[destIdx];
  const ring  = svgEl("circle", {
    cx: String(dest.x), cy: String(dest.y), r: "10",
    fill: destFill,
    stroke: "rgba(255,255,255,0.85)",
    "stroke-width": "2.5",
  }) as SVGCircleElement;
  ring.style.cssText =
    `opacity:0;transform-origin:${dest.x}px ${dest.y}px;` +
    `animation:journey-fade-in 0.5s ease ${delay}s forwards,` +
    `journey-dest-pulse 1.8s ease ${delay + 0.5}s infinite;`;
  svg.appendChild(ring);

  // Add to DOM first so getTotalLength() has valid geometry.
  container.appendChild(svg);

  // Now compute accurate curved path lengths and activate animations.
  requestAnimationFrame(() => {
    for (const { maskPath, delay: d, dur } of pending) {
      const len = maskPath.getTotalLength();
      maskPath.style.strokeDasharray = `${len}`;
      maskPath.style.strokeDashoffset = `${len}`;
      maskPath.style.animation = `journey-reveal ${dur}s ease ${d}s forwards`;
    }
  });
}

// ── Tiny SVG element factory ───────────────────────────────────────────────────

function svgEl(tag: string, attrs: Record<string, string>): SVGElement {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}
